import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  useToast,
  Button,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  IconButton,
  Card,
  CardBody,
  FormErrorMessage,
  Skeleton,
  Text,
  Checkbox,
  CheckboxGroup,
  Wrap,
  WrapItem,
  Badge,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { BsArrowLeft, BsPlus, BsTrash } from "react-icons/bs";
import procurementApi from "../services/procurementApi";
import { employeeService } from "../../employee/employeeService";

const WorkflowEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [stages, setStages] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchWorkflow();
    fetchEmployees();
  }, [id]);

  const fetchEmployees = async () => {
    try {
      const data = await employeeService.getAllEmployees();
      setEmployees(data || []);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast({
        title: "Warning",
        description: "Could not load employees for approver selection",
        status: "warning",
        duration: 3000,
      });
    }
  };

  const fetchWorkflow = async () => {
    try {
      setFetchLoading(true);
      const response = await procurementApi.getWorkflows();
      const workflow = (response?.data || response || []).find(
        (w) => w._id === id,
      );
      if (!workflow) {
        throw new Error("Workflow not found");
      }
      setFormData({
        name: workflow.name || "",
        description: workflow.description || "",
      });
      setStages(
        workflow.stages?.map((stage, index) => ({
          _id: stage._id,
          stageName: stage.stageName || "",
          approverType:
            stage.approverUsers?.length > 0
              ? "users"
              : (stage.approverType === "ROLE"
                  ? "role"
                  : stage.approverType?.toLowerCase()) || "role",
          approverRole: stage.approverRole || "",
          approverUsers: stage.approverUsers || [],
          order:
            stage.stageNumber !== undefined
              ? stage.stageNumber + 1
              : stage.order || index + 1,
          isRequired: stage.isRequired !== undefined ? stage.isRequired : true,
          requireAllApprovers: stage.requireAllApprovers || false,
        })) || [],
      );
    } catch (error) {
      toast({
        title: "Error fetching workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const addStage = () => {
    setStages([
      ...stages,
      {
        stageName: "",
        approverType: "role",
        approverRole: "",
        approverUsers: [],
        order: stages.length + 1,
        requireAllApprovers: false,
        isRequired: true,
      },
    ]);
  };

  const updateStage = (index, field, value) => {
    setStages(
      stages.map((stage, i) =>
        i === index ? { ...stage, [field]: value } : stage,
      ),
    );
  };

  const removeStage = (index) => {
    setStages(stages.filter((_, i) => i !== index));
    // Reorder remaining stages
    const reorderedStages = stages
      .filter((_, i) => i !== index)
      .map((stage, i) => ({ ...stage, order: i + 1 }));
    setStages(reorderedStages);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Workflow name must be at least 3 characters";
    }

    if (stages.length === 0) {
      newErrors.stages = "At least one stage is required";
      toast({
        title: "Validation Error",
        description: "Please add at least one approval stage",
        status: "error",
        duration: 3000,
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const workflowData = {
        name: formData.name,
        description: formData.description,
        stages: stages.map((stage) => {
          const stageData = {
            stageName: stage.stageName,
            approverType: stage.approverType.toUpperCase(),
            requireAllApprovers: stage.requireAllApprovers || false,
          };

          // Only include approverRole for role-based approvers
          if (stage.approverType === "role") {
            stageData.approverRole = stage.approverRole;
            stageData.approverUsers = [];
          } else {
            // Only include approverUsers for user-based approvers
            stageData.approverUsers = stage.approverUsers || [];
            stageData.approverRole = "";
          }

          return stageData;
        }),
      };
      await procurementApi.updateWorkflow(id, workflowData);
      toast({
        title: "Workflow updated successfully",
        status: "success",
        duration: 3000,
      });
      navigate("/procurement/workflows");
    } catch (error) {
      toast({
        title: "Error updating workflow",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/procurement/workflows");
  };

  if (fetchLoading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={4} />
        <Skeleton height="600px" />
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack mb={6}>
        <Button
          leftIcon={<BsArrowLeft />}
          variant="ghost"
          onClick={handleCancel}
        >
          Back
        </Button>
        <Heading size="lg">Edit Workflow</Heading>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" borderWidth="1px">
        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Workflow Details */}
            <FormControl isInvalid={errors.name} isRequired>
              <FormLabel>Workflow Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Standard Procurement Approval"
              />
              <FormErrorMessage>{errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Describe this workflow"
                rows={3}
              />
            </FormControl>

            {/* Approval Stages */}
            <Box>
              <HStack justify="space-between" mb={4}>
                <Heading size="sm">Approval Stages</Heading>
                <Button
                  leftIcon={<BsPlus />}
                  size="sm"
                  onClick={addStage}
                  variant="outline"
                  colorScheme="blue"
                >
                  Add Stage
                </Button>
              </HStack>

              <VStack spacing={4} align="stretch">
                {stages.map((stage, index) => (
                  <Card key={stage._id || `new-stage-${index}`}>
                    <CardBody>
                      <VStack align="stretch" spacing={4}>
                        <HStack align="start" spacing={4}>
                          <FormControl flex={1} isRequired>
                            <FormLabel fontSize="sm">Stage Name</FormLabel>
                            <Input
                              size="sm"
                              value={stage.stageName}
                              onChange={(e) =>
                                updateStage(index, "stageName", e.target.value)
                              }
                              placeholder="e.g., Manager Approval"
                            />
                          </FormControl>

                          <FormControl maxW="150px">
                            <FormLabel fontSize="sm">Approver Type</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverType || "role"}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "approverType",
                                  e.target.value,
                                )
                              }
                            >
                              <option value="role">By Role</option>
                              <option value="users">Specific Users</option>
                            </Select>
                          </FormControl>

                          <FormControl maxW="100px">
                            <FormLabel fontSize="sm">Order</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={stage.order}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "order",
                                  parseInt(e.target.value) || 1,
                                )
                              }
                            />
                          </FormControl>

                          <IconButton
                            mt={7}
                            size="sm"
                            icon={<BsTrash />}
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => removeStage(index)}
                            aria-label="Remove stage"
                            isDisabled={stages.length === 1}
                          />
                        </HStack>

                        {/* Approver Role Selection */}
                        {(stage.approverType === "role" ||
                          !stage.approverType) && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">Approver Role</FormLabel>
                            <Select
                              size="sm"
                              value={stage.approverRole}
                              onChange={(e) =>
                                updateStage(
                                  index,
                                  "approverRole",
                                  e.target.value,
                                )
                              }
                              placeholder="Select role"
                            >
                              <option value="SuperAdmin">Super Admin</option>
                              <option value="Admin">Admin</option>
                              <option value="HR">HR</option>
                              <option value="Manager">Manager</option>
                              <option value="Employee">Employee</option>
                            </Select>
                          </FormControl>
                        )}

                        {/* Approver Users Selection */}
                        {stage.approverType === "users" && (
                          <FormControl isRequired>
                            <FormLabel fontSize="sm">
                              Select Approver User
                              {stage.approverUsers?.length > 0 && (
                                <Badge ml={2} colorScheme="blue">
                                  {stage.approverUsers.length} selected
                                </Badge>
                              )}
                            </FormLabel>
                            {employees.length === 0 ? (
                              <Text fontSize="sm" color="gray.500">
                                No employees found. Please add employees first.
                              </Text>
                            ) : (
                              <VStack spacing={2} align="stretch">
                                <Select
                                  size="sm"
                                  placeholder="Select an approver"
                                  onChange={(e) => {
                                    if (
                                      e.target.value &&
                                      !stage.approverUsers.includes(
                                        e.target.value,
                                      )
                                    ) {
                                      updateStage(index, "approverUsers", [
                                        ...stage.approverUsers,
                                        e.target.value,
                                      ]);
                                      e.target.value = "";
                                    }
                                  }}
                                >
                                  {employees
                                    .filter(
                                      (emp) =>
                                        !stage.approverUsers.includes(emp._id),
                                    )
                                    .map((emp) => (
                                      <option key={emp._id} value={emp._id}>
                                        {emp.firstName} {emp.lastName}
                                        {emp.role ? ` (${emp.role})` : ""}
                                      </option>
                                    ))}
                                </Select>

                                {stage.approverUsers &&
                                  stage.approverUsers.length > 0 && (
                                    <VStack align="stretch" spacing={2} mt={2}>
                                      <Text fontSize="xs" fontWeight="medium">
                                        Selected Approvers:
                                      </Text>
                                      <VStack
                                        align="stretch"
                                        spacing={1}
                                        pl={2}
                                      >
                                        {stage.approverUsers.map((userId) => {
                                          const emp = employees.find(
                                            (e) => e._id === userId,
                                          );
                                          return (
                                            <HStack
                                              key={userId}
                                              justify="space-between"
                                              fontSize="sm"
                                              p={2}
                                              bg="gray.50"
                                              borderRadius="md"
                                            >
                                              <Text>
                                                {emp?.firstName} {emp?.lastName}
                                                {emp?.role && (
                                                  <Text
                                                    as="span"
                                                    fontSize="xs"
                                                    color="gray.500"
                                                    ml={1}
                                                  >
                                                    ({emp.role})
                                                  </Text>
                                                )}
                                              </Text>
                                              <IconButton
                                                icon={<BsTrash />}
                                                size="xs"
                                                variant="ghost"
                                                colorScheme="red"
                                                onClick={() =>
                                                  updateStage(
                                                    index,
                                                    "approverUsers",
                                                    stage.approverUsers.filter(
                                                      (id) => id !== userId,
                                                    ),
                                                  )
                                                }
                                                aria-label="Remove approver"
                                              />
                                            </HStack>
                                          );
                                        })}
                                      </VStack>
                                    </VStack>
                                  )}
                              </VStack>
                            )}
                          </FormControl>
                        )}
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </VStack>
            </Box>

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end" pt={4}>
              <Button
                variant="outline"
                onClick={handleCancel}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" colorScheme="blue" isLoading={loading}>
                Update Workflow
              </Button>
            </HStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default WorkflowEdit;
